import React from 'react';
import { useTranslation } from 'react-i18next';
import { useModules } from '@/context/ModuleContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * ModuleGuard - Wraps content that requires a specific module
 * Shows upgrade prompt if user doesn't have access
 * 
 * @param {string} module - The module code required
 * @param {React.ReactNode} children - Content to show if user has access
 * @param {React.ReactNode} fallback - Custom fallback (optional)
 * @param {boolean} preview - If true, shows a preview with blur effect
 * @param {string} featureName - Human-readable feature name for the prompt
 */
export function ModuleGuard({ 
  module, 
  children, 
  fallback, 
  preview = false,
  featureName 
}) {
  const { hasModule, getModuleInfo, loading } = useModules();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-lg p-4 h-32">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  if (hasModule(module)) {
    return children;
  }

  // Get module info for display
  const moduleInfo = getModuleInfo(module);
  const displayName = featureName || moduleInfo?.name || module;
  const icon = moduleInfo?.icon || '🔒';

  // Custom fallback provided
  if (fallback) {
    return fallback;
  }

  // Preview mode - show blurred content with overlay
  if (preview) {
    return (
      <div className="relative">
        <div className="blur-sm pointer-events-none select-none opacity-50">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
          <div className="text-center p-6">
            <div className="text-4xl mb-2">{icon}</div>
            <h3 className="font-semibold text-lg mb-1">{displayName}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('modules.upgradeRequired')}
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              {t('modules.upgradePlan')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Default upgrade card
  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardContent className="p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="font-semibold text-lg mb-2">
          {icon} {displayName}
        </h3>
        <p className="text-muted-foreground mb-4 text-sm">
          {t('modules.upgradeRequired')}
        </p>
        <Button 
          onClick={() => navigate('/subscription')}
          className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
        >
          <Sparkles className="h-4 w-4" />
          {t('modules.upgradePlan')}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * useModuleCheck - Hook to check module access
 * Returns { hasAccess, isLoading, moduleInfo }
 */
export function useModuleCheck(moduleCode) {
  const { hasModule, getModuleInfo, loading } = useModules();
  
  return {
    hasAccess: hasModule(moduleCode),
    isLoading: loading,
    moduleInfo: getModuleInfo(moduleCode)
  };
}
